// Login — four modes wrapped in one screen (no route drilling — auth
// happens before the router mounts the authed tree). Modes:
//   'password' (default)  → username + password
//   'phone'               → phone OTP (kept per "1a" decision; fallback for
//                            existing users who haven't set a password)
//   'reset'               → forgot-password: username → SMS code → new pw
//   'join'                → invitation-backed signup without OTP; collect
//                            name, email, phone, invitation and credentials atomically
//
// Successful sign-in flips global auth state (via useAuth().signIn), which
// causes App.tsx to re-render into the authed tree. No navigate() calls
// needed here.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi, ApiError } from '../lib/api';
import { getOrCreateDeviceUuid } from '../lib/storage';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';

const PHONE_RE = /^\+[1-9]\d{7,14}$/;

type Mode = 'password' | 'phone' | 'reset' | 'join';

type PhoneStep =
    | { kind: 'phone' }
    | { kind: 'code'; phone: string };

type ResetStep =
    | { kind: 'username' }
    | { kind: 'code'; username: string; phoneHint: string };

export function LoginScreen() {
    const [mode, setMode] = useState<Mode>('password');
    return (
        <div className="min-h-full flex flex-col">
            <header className="px-6 py-4">
                <Logo size={32} />
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="pm-card w-full max-w-sm p-6">
                    <div className="text-brand-700 text-xs uppercase tracking-wide font-semibold">
                        Pamosi Clinic
                    </div>
                    {mode === 'password' && <PasswordPane onSwitchMode={setMode} />}
                    {mode === 'phone' && <PhonePane onSwitchMode={setMode} />}
                    {mode === 'reset' && <ResetPane onSwitchMode={setMode} />}
                    {mode === 'join' && <JoinPane onSwitchMode={setMode} />}
                </div>
            </div>
        </div>
    );
}

// ─── Password (default) ──────────────────────────────────────────────────

function PasswordPane({ onSwitchMode }: { onSwitchMode: (m: Mode) => void }) {
    const { signIn } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        if (!username.trim() || !password) {
            setError('Enter your username and password.');
            return;
        }
        setBusy(true);
        try {
            const res = await authApi.loginPassword({
                username: username.trim(),
                password,
                deviceUuid: getOrCreateDeviceUuid(),
            });
            signIn({
                accessToken: res.access_token,
                refreshToken: res.refresh_token,
                clinician: res.clinician,
            });
        } catch (err) {
            setError(err instanceof ApiError && err.code === 'invalid_credentials'
                ? 'Wrong username or password.'
                : err instanceof ApiError && err.code === 'too_many_requests'
                    ? 'Too many attempts. Try again in a minute.'
                    : 'Could not sign in. Try again.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <h1 className="text-slate-900 text-xl font-semibold mt-1">Sign in</h1>
            <p className="text-slate-500 text-sm mt-1">
                Enter your Pamosi Clinic username and password.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                    <label className="pm-label" htmlFor="username">Username</label>
                    <input
                        id="username"
                        className="pm-input"
                        type="text"
                        autoComplete="username"
                        placeholder="e.g. drfemi or you@clinic.com"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={busy}
                    />
                </div>
                <div>
                    <label className="pm-label" htmlFor="password">Password</label>
                    <input
                        id="password"
                        className="pm-input"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={busy}
                    />
                </div>
                {error && <div className="pm-error">{error}</div>}
                <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                    {busy ? 'Signing in…' : 'Sign in'}
                </button>
                <div className="flex items-center justify-between text-sm">
                    <button
                        type="button"
                        className="text-brand-700 hover:underline"
                        onClick={() => onSwitchMode('reset')}
                        disabled={busy}
                    >
                        Forgot password?
                    </button>
                    <button
                        type="button"
                        className="text-slate-600 hover:underline"
                        onClick={() => onSwitchMode('phone')}
                        disabled={busy}
                    >
                        Use phone code
                    </button>
                </div>
            </form>
            <div className="mt-6 border-t border-slate-200 pt-4 text-center">
                <div className="text-slate-500 text-xs mb-2">
                    New to Pamosi Clinic?
                </div>
                <button
                    type="button"
                    className="pm-btn-ghost w-full border border-slate-200"
                    onClick={() => onSwitchMode('join')} disabled={busy}
                >
                    Join a clinic with invite code
                </button>
                <div className="mt-3 text-slate-500 text-xs">
                    Setting up a new clinic or hospital?{' '}
                    <Link
                        to="/register-workspace"
                        className="text-brand-700 hover:underline font-medium"
                    >
                        Register a new workspace
                    </Link>
                </div>
            </div>
        </>
    );
}

// ─── Phone OTP (fallback) ────────────────────────────────────────────────

function PhonePane({ onSwitchMode }: { onSwitchMode: (m: Mode) => void }) {
    const { signIn } = useAuth();
    const [step, setStep] = useState<PhoneStep>({ kind: 'phone' });
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function submitPhone(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        if (!PHONE_RE.test(phone)) {
            setError('Enter a valid phone in international format (e.g. +2348012345678).');
            return;
        }
        setBusy(true);
        try {
            await authApi.requestOtp(phone);
            setStep({ kind: 'code', phone });
        } catch (err) {
            setError(err instanceof ApiError && err.code === 'invalid_phone'
                ? 'That phone number is not valid.'
                : 'Could not send code. Try again.');
        } finally {
            setBusy(false);
        }
    }

    async function submitCode(e: React.FormEvent) {
        e.preventDefault();
        if (step.kind !== 'code') return;
        setError(null);
        if (!/^\d{4,8}$/.test(code)) {
            setError('Enter the code from your SMS.');
            return;
        }
        setBusy(true);
        try {
            const res = await authApi.verifyOtp({
                phoneE164: step.phone,
                code,
                deviceUuid: getOrCreateDeviceUuid(),
            });
            signIn({
                accessToken: res.access_token,
                refreshToken: res.refresh_token,
                clinician: res.clinician,
            });
        } catch (err) {
            setError(err instanceof ApiError && err.status === 401
                ? 'That code is wrong. Try again.'
                : 'Could not verify code. Try again.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <h1 className="text-slate-900 text-xl font-semibold mt-1">Sign in with phone code</h1>
            <p className="text-slate-500 text-sm mt-1">
                {step.kind === 'phone'
                    ? 'We\u2019ll send a verification code to your clinic phone.'
                    : `We sent a code to ${step.phone}.`}
            </p>

            {step.kind === 'phone' && (
                <form onSubmit={submitPhone} className="mt-6 space-y-4">
                    <div>
                        <label className="pm-label" htmlFor="phone">Phone number</label>
                        <input
                            id="phone"
                            className="pm-input"
                            type="tel"
                            autoComplete="tel"
                            placeholder="+2348012345678"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.trim())}
                            disabled={busy}
                        />
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                        {busy ? 'Sending…' : 'Send code'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost w-full"
                        onClick={() => onSwitchMode('password')}
                        disabled={busy}
                    >
                        Back to username & password
                    </button>
                </form>
            )}

            {step.kind === 'code' && (
                <form onSubmit={submitCode} className="mt-6 space-y-4">
                    <div>
                        <label className="pm-label" htmlFor="code">Verification code</label>
                        <input
                            id="code"
                            className="pm-input tracking-widest text-center text-lg"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={8}
                            placeholder="\u2022\u2022\u2022\u2022\u2022\u2022"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                            disabled={busy}
                        />
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                        {busy ? 'Verifying…' : 'Verify & sign in'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost w-full"
                        onClick={() => { setStep({ kind: 'phone' }); setCode(''); setError(null); }}
                        disabled={busy}
                    >
                        Use a different number
                    </button>
                </form>
            )}
        </>
    );
}

// ─── Forgot password (username → SMS code → new password) ────────────────

function ResetPane({ onSwitchMode }: { onSwitchMode: (m: Mode) => void }) {
    const { signIn } = useAuth();
    const [step, setStep] = useState<ResetStep>({ kind: 'username' });
    const [username, setUsername] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function submitUsername(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        const uname = username.trim();
        if (!uname) {
            setError('Enter your username.');
            return;
        }
        setBusy(true);
        try {
            const res = await authApi.requestPasswordReset(uname);
            setStep({ kind: 'code', username: uname, phoneHint: res.phone_hint });
        } catch (err) {
            setError(err instanceof ApiError && err.code === 'too_many_requests'
                ? 'Too many attempts. Try again in a minute.'
                : 'Could not send reset code. Try again.');
        } finally {
            setBusy(false);
        }
    }

    async function submitReset(e: React.FormEvent) {
        e.preventDefault();
        if (step.kind !== 'code') return;
        setError(null);
        if (!/^\d{4,8}$/.test(code)) {
            setError('Enter the code from your SMS.');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters, with a letter and a number.');
            return;
        }
        setBusy(true);
        try {
            const res = await authApi.verifyPasswordReset({
                username: step.username,
                code,
                newPassword,
                deviceUuid: getOrCreateDeviceUuid(),
            });
            signIn({
                accessToken: res.access_token,
                refreshToken: res.refresh_token,
                clinician: res.clinician,
            });
        } catch (err) {
            const code = err instanceof ApiError ? err.code : '';
            setError(
                code === 'invalid_code' ? 'That code is wrong or expired. Try again.'
                : code === 'weak_password' ? 'Password too weak. Use 8+ characters, a letter, and a number.'
                : 'Could not reset password. Try again.',
            );
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <h1 className="text-slate-900 text-xl font-semibold mt-1">Reset password</h1>
            <p className="text-slate-500 text-sm mt-1">
                {step.kind === 'username'
                    ? 'We\u2019ll send a verification code to the phone on file for your username.'
                    : `We sent a code to ${step.phoneHint}. Enter it below and pick a new password.`}
            </p>

            {step.kind === 'username' && (
                <form onSubmit={submitUsername} className="mt-6 space-y-4">
                    <div>
                        <label className="pm-label" htmlFor="reset-username">Username</label>
                        <input
                            id="reset-username"
                            className="pm-input"
                            type="text"
                            autoComplete="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                        {busy ? 'Sending…' : 'Send reset code'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost w-full"
                        onClick={() => onSwitchMode('password')}
                        disabled={busy}
                    >
                        Back to sign in
                    </button>
                </form>
            )}

            {step.kind === 'code' && (
                <form onSubmit={submitReset} className="mt-6 space-y-4">
                    <div>
                        <label className="pm-label" htmlFor="reset-code">Verification code</label>
                        <input
                            id="reset-code"
                            className="pm-input tracking-widest text-center text-lg"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={8}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="reset-new-pw">New password</label>
                        <input
                            id="reset-new-pw"
                            className="pm-input"
                            type="password"
                            autoComplete="new-password"
                            placeholder="At least 8 characters"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                        {busy ? 'Resetting…' : 'Reset password & sign in'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost w-full"
                        onClick={() => { setStep({ kind: 'username' }); setCode(''); setNewPassword(''); setError(null); }}
                        disabled={busy}
                    >
                        Use a different username
                    </button>
                </form>
            )}
        </>
    );
}

// Invitation-backed signup: account, role, session and redemption are atomic.

function JoinPane({ onSwitchMode }: { onSwitchMode: (m: Mode) => void }) {
    const { signIn } = useAuth();
    const [form,setForm] = useState({code:'',fullName:'',email:'',phone:'',username:'',password:'',confirm:''});
    const [busy,setBusy] = useState(false), [error,setError] = useState<string | null>(null);
    async function submit(e: React.FormEvent) {
        e.preventDefault(); setError(null);
        if (form.password !== form.confirm) {setError('Passwords do not match.'); return;}
        if (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {setError('Use at least 8 characters, including a letter and a number.'); return;}
        if (!/^[A-Za-z0-9._+@-]{3,64}$/.test(form.username.trim())) {setError('Username must be 3–64 characters without spaces.'); return;}
        setBusy(true);
        try {
            const res = await authApi.signupWithInvite({...form,deviceUuid:getOrCreateDeviceUuid()});
            signIn({accessToken:res.access_token,refreshToken:res.refresh_token,clinician:res.clinician});
        } catch (err) {
            const code = err instanceof ApiError ? err.code : '';
            setError(code === 'account_already_exists' ? 'An account already uses these details. Sign in to your existing account; ask your admin if you need help.' : code === 'invalid_or_expired_code' ? 'This invitation is invalid, expired or already used. Ask your admin for a new code.' : code === 'too_many_requests' ? 'Too many attempts. Please try again later.' : err instanceof Error ? err.message : 'Could not create the account. Try again.');
        } finally {setBusy(false);}
    }
    return <><h1 className="text-slate-900 text-xl font-semibold mt-1">Join your clinic</h1><p className="text-slate-500 text-sm mt-1">Enter the invitation from your admin and create your sign-in details. No OTP is required.</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
            {([{key:'code',label:'Invitation code',type:'text',autocomplete:'off',max:8,pattern:'[A-Za-z0-9]{8}'},{key:'fullName',label:'Full name',type:'text',autocomplete:'name',max:160},{key:'email',label:'Email address',type:'email',autocomplete:'email',max:320},{key:'phone',label:'Telephone number',type:'tel',autocomplete:'tel',max:16,pattern:'\\+[1-9][0-9]{7,14}',placeholder:'+2348012345678'},{key:'username',label:'Username',type:'text',autocomplete:'username',max:64,pattern:'[A-Za-z0-9._+@\\-]{3,64}'},{key:'password',label:'Password',type:'password',autocomplete:'new-password',max:200},{key:'confirm',label:'Confirm password',type:'password',autocomplete:'new-password',max:200}] as const).map(f=><label key={f.key} className="block"><span className="pm-label">{f.label}</span><input className="pm-input" required type={f.type} autoComplete={f.autocomplete} maxLength={f.max} pattern={'pattern' in f ? f.pattern : undefined} placeholder={'placeholder' in f ? f.placeholder : undefined} value={form[f.key]} disabled={busy} onChange={e=>setForm({...form,[f.key]:e.target.value})} /></label>)}
            <p className="text-xs text-slate-500">Use 8 or more characters with a letter and a number. Your admin's invitation determines your clinic and role.</p>
            {error && <div className="pm-error" role="alert">{error}</div>}
            <button type="submit" className="pm-btn-primary w-full" disabled={busy}>{busy ? 'Creating account…' : 'Create account & join clinic'}</button>
            <button type="button" className="pm-btn-ghost w-full" disabled={busy} onClick={()=>onSwitchMode('password')}>I already have an account</button>
        </form></>;
}
